import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../../components/Navbar';
import Footer from '../../components/Footer';
import { AuthService } from '../../services/authService';
import { logger } from '../../utils/logger';
import type { ResetStatus } from './types';
import { passwordRequirements } from './types';
import { getStatusIcon, getIconBackground, getTitle, getSubtitle } from './StatusDisplay';
import { InfoSidebar } from './InfoSidebar';
import { PasswordForm } from './PasswordForm';
import { LoadingProgress, SuccessState, ErrorState } from './ResultStates';

export const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [status, setStatus] = useState<ResetStatus>('validating');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [confirmError, setConfirmError] = useState<string>('');
  const redirectTimerRef = useRef<number | null>(null);

  // Prevent double-invocation in React StrictMode
  const validationAttempted = useRef(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No reset token provided. Please request a new password reset link.');
      return;
    }

    if (token.length !== 64) {
      setStatus('error');
      setErrorMessage('Invalid reset token format. Please request a new password reset link.');
      return;
    }

    if (!validationAttempted.current) {
      validationAttempted.current = true;
      validateToken(token);
    }
  }, [token]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current !== null) {
        window.clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const validateToken = async (resetToken: string) => {
    try {
      const response = await AuthService.validateResetToken(resetToken);
      if (response.data?.valid) {
        setStatus('ready');
      } else {
        setStatus('error');
        setErrorMessage(
          'Invalid or expired reset token. Please request a new password reset link.',
        );
      }
    } catch (error: unknown) {
      logger.error('Token validation error', { error });
      const responseError = axios.isAxiosError(error)
        ? (error.response?.data as Record<string, string> | undefined)?.error
        : undefined;
      if (responseError?.includes('expired')) {
        setStatus('expired');
        setErrorMessage('Your password reset link has expired. Please request a new one.');
      } else {
        setStatus('error');
        setErrorMessage(
          responseError ||
            'Invalid or expired reset token. Please request a new password reset link.',
        );
      }
    }
  };

  const validatePassword = (value: string): boolean => {
    const allMet = passwordRequirements.every((req) => req.test(value));
    if (!allMet) {
      setPasswordError('Password does not meet all requirements');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (value: string): boolean => {
    if (value !== password) {
      setConfirmError('Passwords do not match');
      return false;
    }
    setConfirmError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isPasswordValid = validatePassword(password);
    const isConfirmValid = validateConfirmPassword(confirmPassword);

    if (!isPasswordValid || !isConfirmValid) {
      return;
    }

    if (!token) {
      setStatus('error');
      setErrorMessage('No reset token found. Please request a new password reset link.');
      return;
    }

    setStatus('submitting');

    try {
      await AuthService.resetPassword(token, password);

      // Server sets httpOnly auth cookies — no client-side token storage needed

      setStatus('success');

      // Redirect to dashboard after a delay (user is now logged in)
      // Use window.location for full page reload so JWTAuthContext re-initializes with new tokens
      redirectTimerRef.current = window.setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2000);
    } catch (error: unknown) {
      logger.error('Password reset error', { error });
      const responseData = axios.isAxiosError(error)
        ? (error.response?.data as Record<string, string> | undefined)
        : undefined;
      const errorMsg = responseData?.error || 'Failed to reset password. Please try again.';

      if (errorMsg.includes('expired') || responseData?.code === 'INVALID_TOKEN') {
        setStatus('expired');
        setErrorMessage('Your password reset link has expired. Please request a new one.');
      } else {
        setStatus('error');
        setErrorMessage(errorMsg);
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar showAuthButton={false} />

      <main className="flex-1 px-6 lg:px-8 py-12 pt-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <InfoSidebar status={status} />

            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <div
                className="bg-white dark:bg-gray-800 rounded-xl p-8"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-default)',
                }}
              >
                <div className="flex justify-center mb-6">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: getIconBackground(status) }}
                  >
                    {getStatusIcon(status)}
                  </div>
                </div>

                <div className="text-center mb-6">
                  <h2
                    className="text-2xl font-semibold mb-2"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {getTitle(status)}
                  </h2>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {getSubtitle(status, errorMessage)}
                  </p>
                </div>

                {(status === 'validating' || status === 'submitting') && <LoadingProgress />}

                {status === 'ready' && (
                  <PasswordForm
                    password={password}
                    confirmPassword={confirmPassword}
                    showPassword={showPassword}
                    showConfirmPassword={showConfirmPassword}
                    passwordError={passwordError}
                    confirmError={confirmError}
                    setPassword={setPassword}
                    setConfirmPassword={setConfirmPassword}
                    setShowPassword={setShowPassword}
                    setShowConfirmPassword={setShowConfirmPassword}
                    validatePassword={validatePassword}
                    validateConfirmPassword={validateConfirmPassword}
                    onSubmit={handleSubmit}
                  />
                )}

                {status === 'success' && <SuccessState />}

                {(status === 'error' || status === 'expired') && (
                  <ErrorState status={status} errorMessage={errorMessage} />
                )}

                <p
                  className="text-xs text-center mt-6"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  Having trouble? Contact us at support@autar.ai
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
